interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
  uploadedParts: number;
  totalParts: number;
  currentPart: number;
  speed?: number;
  remainingTime?: number;
}

interface MultipartUploadOptions {
  file: Blob;
  uploadUrl?: string;
  chunkSize?: number;
  maxConcurrentUploads?: number;
  maxRetries?: number;
  retryDelay?: number;
  onProgress?: (progress: UploadProgress) => void;
  onChunkComplete?: (partNumber: number) => void;
  onChunkError?: (partNumber: number, error: Error) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  headers?: Record<string, string>;
}

interface UploadPart {
  partNumber: number;
  chunk: Blob;
  etag?: string;
  attempts: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
}

export class S3MultipartUpload {
  private static readonly MIN_CHUNK_SIZE = 5 * 1024 * 1024;  // 5MB minimum for S3
  private static readonly DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB default
  private static readonly MAX_CONCURRENT_UPLOADS = 3;
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_DELAY = 1000;

  private uploadId?: string;
  private parts: UploadPart[] = [];
  private completedParts: { PartNumber: number; ETag: string }[] = [];
  private uploadStartTime?: number;
  private bytesUploaded = 0;
  private lastProgressUpdate = 0;
  private abortController: AbortController;
  private activeUploads = 0;
  private paused = false;

  private options: Required<MultipartUploadOptions>;

  constructor(options: MultipartUploadOptions) {
    this.options = {
      ...options,
      chunkSize: Math.max(options.chunkSize || S3MultipartUpload.DEFAULT_CHUNK_SIZE, S3MultipartUpload.MIN_CHUNK_SIZE),
      maxConcurrentUploads: options.maxConcurrentUploads || S3MultipartUpload.MAX_CONCURRENT_UPLOADS,
      maxRetries: options.maxRetries ?? S3MultipartUpload.MAX_RETRIES,
      retryDelay: options.retryDelay || S3MultipartUpload.RETRY_DELAY,
      uploadUrl: options.uploadUrl || '',
      onProgress: options.onProgress || (() => {}),
      onChunkComplete: options.onChunkComplete || (() => {}),
      onChunkError: options.onChunkError || (() => {}),
      onComplete: options.onComplete || (() => {}),
      onError: options.onError || (() => {}),
      headers: options.headers || {}
    };

    this.abortController = new AbortController();
    this.initializeParts();
  }

  private initializeParts() {
    const file = this.options.file;
    const chunkSize = this.options.chunkSize;
    const totalParts = Math.ceil(file.size / chunkSize);

    for (let i = 0; i < totalParts; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      this.parts.push({
        partNumber: i + 1,
        chunk,
        attempts: 0,
        status: 'pending'
      });
    }
  }

  // Direct upload to presigned URL - no multipart initialization needed
  async uploadDirect(): Promise<void> {
    if (!this.options.uploadUrl) {
      throw new Error('Upload URL is required for direct upload');
    }

    this.uploadStartTime = Date.now();

    try {
      // For files smaller than chunk size, do single upload
      if (this.options.file.size <= this.options.chunkSize) {
        await this.uploadSingleFile();
      } else {
        // For larger files, chunk and upload sequentially or in parallel
        await this.uploadChunkedFile();
      }

      this.options.onComplete();
    } catch (error) {
      this.options.onError(error as Error);
      throw error;
    }
  }

  private async uploadSingleFile(): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 min timeout

    try {
      const response = await this.uploadWithRetry(
        this.options.uploadUrl!,
        this.options.file,
        0,
        controller.signal
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      this.bytesUploaded = this.options.file.size;
      this.updateProgress();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async uploadChunkedFile(): Promise<void> {
    const uploadQueue: Promise<void>[] = [];

    for (const part of this.parts) {
      if (this.paused || this.abortController.signal.aborted) break;

      // Wait if we have too many concurrent uploads
      while (this.activeUploads >= this.options.maxConcurrentUploads) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const uploadPromise = this.uploadPart(part).then(() => {
        this.activeUploads--;
      }).catch((error) => {
        this.activeUploads--;
        throw error;
      });

      this.activeUploads++;
      uploadQueue.push(uploadPromise);

      // For better concurrency control
      if (uploadQueue.length >= this.options.maxConcurrentUploads) {
        await Promise.race(uploadQueue);
      }
    }

    // Wait for all uploads to complete
    await Promise.all(uploadQueue);
  }

  private async uploadPart(part: UploadPart): Promise<void> {
    part.status = 'uploading';

    try {
      const response = await this.uploadWithRetry(
        this.options.uploadUrl!,
        part.chunk,
        part.partNumber,
        this.abortController.signal
      );

      if (!response.ok) {
        throw new Error(`Part ${part.partNumber} upload failed: ${response.status}`);
      }

      part.status = 'completed';
      part.etag = response.headers.get('ETag') || `"part-${part.partNumber}"`;

      this.completedParts.push({
        PartNumber: part.partNumber,
        ETag: part.etag
      });

      this.bytesUploaded += part.chunk.size;
      this.updateProgress();
      this.options.onChunkComplete(part.partNumber);

    } catch (error) {
      part.status = 'failed';
      this.options.onChunkError(part.partNumber, error as Error);
      throw error;
    }
  }

  private async uploadWithRetry(
    url: string,
    data: Blob,
    partNumber: number,
    signal: AbortSignal
  ): Promise<Response> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < this.options.maxRetries; attempt++) {
      try {
        // Convert Blob to ArrayBuffer to have better control over Content-Type
        const arrayBuffer = await data.arrayBuffer();

        const response = await fetch(url, {
          method: 'PUT',
          body: arrayBuffer,
          signal,
          headers: {
            'Content-Type': 'audio/flac', // Match backend's presigned URL expectation
            ...(this.options.headers || {})
          }
        });

        if (response.ok) {
          return response;
        }

        // Retry on 5xx errors
        if (response.status >= 500) {
          lastError = new Error(`Server error: ${response.status}`);
          await this.delay(this.options.retryDelay * Math.pow(2, attempt));
          continue;
        }

        // Don't retry on client errors
        return response;

      } catch (error) {
        lastError = error as Error;

        // Don't retry on abort
        if (signal.aborted) {
          throw error;
        }

        // Exponential backoff
        await this.delay(this.options.retryDelay * Math.pow(2, attempt));
      }
    }

    throw lastError || new Error('Upload failed after retries');
  }

  private updateProgress() {
    const now = Date.now();
    const timeDiff = (now - this.lastProgressUpdate) / 1000;

    // Throttle progress updates to every 100ms
    if (timeDiff < 0.1 && this.bytesUploaded < this.options.file.size) {
      return;
    }

    this.lastProgressUpdate = now;

    const progress: UploadProgress = {
      loaded: this.bytesUploaded,
      total: this.options.file.size,
      percentage: Math.round((this.bytesUploaded / this.options.file.size) * 100),
      uploadedParts: this.completedParts.length,
      totalParts: this.parts.length,
      currentPart: this.parts.findIndex(p => p.status === 'uploading') + 1
    };

    // Calculate upload speed
    if (this.uploadStartTime) {
      const elapsedSeconds = (now - this.uploadStartTime) / 1000;
      progress.speed = this.bytesUploaded / elapsedSeconds;

      const remainingBytes = this.options.file.size - this.bytesUploaded;
      progress.remainingTime = remainingBytes / progress.speed;
    }

    this.options.onProgress(progress);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  pause() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
    // Re-trigger upload for pending parts
    this.uploadChunkedFile().catch(error => {
      this.options.onError(error);
    });
  }

  abort() {
    this.abortController.abort();
  }

  getFailedParts(): number[] {
    return this.parts
      .filter(p => p.status === 'failed')
      .map(p => p.partNumber);
  }

  async retryFailedParts(): Promise<void> {
    const failedParts = this.parts.filter(p => p.status === 'failed');

    for (const part of failedParts) {
      part.status = 'pending';
      part.attempts = 0;
    }

    await this.uploadChunkedFile();
  }

  // Helper method to save audio locally as backup
  static async saveAudioLocally(blob: Blob, filename: string) {
    try {
      // Check if the browser supports the File System Access API
      if ('showSaveFilePicker' in window) {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: 'Audio File',
            accept: { 'audio/webm': ['.webm'] }
          }]
        });

        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return true;
      } else {
        // Fallback to download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        return true;
      }
    } catch (error) {
      console.error('Failed to save audio locally:', error);
      return false;
    }
  }

  // Get upload statistics
  getStatistics() {
    const now = Date.now();
    const elapsedMs = this.uploadStartTime ? now - this.uploadStartTime : 0;

    return {
      bytesUploaded: this.bytesUploaded,
      totalBytes: this.options.file.size,
      completedParts: this.completedParts.length,
      totalParts: this.parts.length,
      failedParts: this.getFailedParts().length,
      elapsedTime: elapsedMs / 1000,
      averageSpeed: elapsedMs > 0 ? this.bytesUploaded / (elapsedMs / 1000) : 0,
      estimatedTimeRemaining: this.calculateRemainingTime()
    };
  }

  private calculateRemainingTime(): number {
    if (!this.uploadStartTime || this.bytesUploaded === 0) return 0;

    const elapsedMs = Date.now() - this.uploadStartTime;
    const uploadSpeed = this.bytesUploaded / (elapsedMs / 1000);
    const remainingBytes = this.options.file.size - this.bytesUploaded;

    return remainingBytes / uploadSpeed;
  }
}

// Utility function for formatting file size
export function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
}

// Utility function for formatting time
export function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  }
}